import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import GridOnIcon from '@mui/icons-material/GridOn';
import LinkIcon from '@mui/icons-material/Link';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import AdjustIcon from '@mui/icons-material/Adjust';
import RuleIcon from '@mui/icons-material/Rule';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import type { SvgIconComponent } from '@mui/icons-material';

import GoogleDrivePickerButton from './GoogleDrivePickerButton';

import type { Rubric, RubricContent } from '@/shared/types/rubric';
import type {
  GenerateFromTemplateRequest,
  GenerateFromTemplateResult,
  Resource,
  ResourceParentLocator,
  ResourceTemplate,
} from '@/shared/types/resource';
import { useAppSelector } from '@/shared/hooks/redux';
import { useGetRubricsByOwnerQuery, useGetSharedRubricsQuery } from '@/features/rubrics/rubricsApi';
import { useAttachLibraryRubricAsResourceMutation } from '@/features/resources/resourcesApi';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface UnifiedResourcePickerDialogProps {
  open: boolean;
  locator: ResourceParentLocator;
  templates: ResourceTemplate[];
  disableGenerate?: boolean;
  rubricsEnabled?: boolean;
  /** Show Google Drive import tile (requires courseId to be set). */
  showGoogleDrive?: boolean;
  /** Show Import Google Slides tile (requires courseId + feature flag). */
  showImportSlides?: boolean;
  /** Show Import Google Docs tile (requires courseId + feature flag). */
  showImportDocs?: boolean;
  onClose: () => void;
  // Template actions
  onPickTemplate: (input: { templateId: string }) => Promise<Resource | void> | Resource | void;
  onGenerateFromTemplate: (
    request: GenerateFromTemplateRequest
  ) => Promise<GenerateFromTemplateResult | void>;
  onCreatedResource?: (resource: Resource) => void;
  onGeneratedResource?: (result: GenerateFromTemplateResult) => void;
  // "New" tile actions — the parent is responsible for closing the picker before opening secondary dialogs
  onNewDocument: () => void;
  onNewSlides: () => void;
  onNewRubric: () => void;
  onUploadFile: () => void;
  onAddLink: () => void;
  // Google Drive error / import-complete pass-through
  onGoogleDriveError?: (message: string | null) => void;
  onNativeImportComplete?: (result: {
    totalWarnings: number;
    importedCount: number;
    failedCount: number;
  }) => void;
}

type SidebarCategoryId = 'new' | 'rubrics' | 'templates';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getGenerationErrorMessage(error: unknown): string {
  const fallback = 'Generation failed. Please try again.';
  if (typeof error === 'string' && error.trim()) return error.trim();
  if (!error || typeof error !== 'object') return fallback;
  const e = error as { data?: { error?: unknown }; error?: unknown; message?: unknown };
  const raw = [
    typeof e.data?.error === 'string' ? e.data.error : null,
    typeof e.error === 'string' ? e.error : null,
    typeof e.message === 'string' ? e.message : null,
  ].find((v): v is string => Boolean(v?.trim()));
  if (!raw) return fallback;
  return raw
    .replace(/^[A-Za-z ]+:\s+Error:\s+/, '')
    .replace(/^Error:\s+/, '')
    .trim();
}

function rubricTypeIcon(content: RubricContent | null): React.ReactElement | null {
  if (!content) return null;
  switch (content.rubricType) {
    case 'analytic':
      return <GridOnIcon fontSize="small" />;
    case 'holistic':
      return <LinearScaleIcon fontSize="small" />;
    case 'single-point':
      return <AdjustIcon fontSize="small" />;
    default:
      return null;
  }
}

function rubricTypeLabel(content: RubricContent | null): string {
  if (!content) return 'Empty';
  switch (content.rubricType) {
    case 'analytic':
      return 'Analytic';
    case 'holistic':
      return 'Holistic';
    case 'single-point':
      return 'Single-Point';
    default:
      return 'Unknown';
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ActionTile({
  icon: Icon,
  label,
  onClick,
  disabled = false,
}: {
  icon: SvgIconComponent;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Paper
      variant="outlined"
      onClick={disabled ? undefined : onClick}
      sx={{
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'center',
        opacity: disabled ? 0.5 : 1,
        minHeight: 100,
        '&:hover': disabled ? {} : { bgcolor: 'action.hover', borderColor: 'primary.main' },
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Icon sx={{ fontSize: 32, color: 'text.secondary' }} />
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </Paper>
  );
}

const TILE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, 1fr)',
    sm: 'repeat(3, 1fr)',
    md: 'repeat(4, 1fr)',
  },
  gap: 1.5,
};

// ── Main component ─────────────────────────────────────────────────────────────

const UnifiedResourcePickerDialog: React.FC<UnifiedResourcePickerDialogProps> = ({
  open,
  locator,
  templates,
  disableGenerate = false,
  rubricsEnabled = false,
  showGoogleDrive = false,
  showImportSlides = false,
  showImportDocs = false,
  onClose,
  onPickTemplate,
  onGenerateFromTemplate,
  onCreatedResource,
  onGeneratedResource,
  onNewDocument,
  onNewSlides,
  onNewRubric,
  onUploadFile,
  onAddLink,
  onGoogleDriveError,
  onNativeImportComplete,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const user = useAppSelector((s) => s.auth.user);
  const { data: myRubrics = [], isFetching: myRubricsFetching } = useGetRubricsByOwnerQuery(
    user?.id ?? '',
    { skip: !user?.id || !open || !rubricsEnabled }
  );
  const { data: sharedRubrics = [], isFetching: sharedRubricsFetching } = useGetSharedRubricsQuery(
    user?.id ?? '',
    { skip: !user?.id || !open || !rubricsEnabled }
  );
  const [attachLibraryRubric, { isLoading: attachingRubric }] =
    useAttachLibraryRubricAsResourceMutation();

  // Sidebar state
  const [activeCategory, setActiveCategory] = useState<SidebarCategoryId>('new');

  // Template section state
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateSort, setTemplateSort] = useState<'title' | 'recent'>('title');

  // Template generation state
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [contextInputId, setContextInputId] = useState<string | null>(null);
  const [userContext, setUserContext] = useState('');
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Rubric section state
  const [rubricSearch, setRubricSearch] = useState('');
  const [rubricAttachError, setRubricAttachError] = useState<string | null>(null);

  // Google Drive import state shown inside this modal, where the user clicked.
  const [googleDriveError, setGoogleDriveError] = useState<string | null>(null);
  const [nativeImportInfo, setNativeImportInfo] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (!open) return;
    setActiveCategory('new');
    setTemplateSearch('');
    setTemplateSort('title');
    setGeneratingId(null);
    setContextInputId(null);
    setUserContext('');
    setGenerationError(null);
    setRubricSearch('');
    setRubricAttachError(null);
    setGoogleDriveError(null);
    setNativeImportInfo(null);
  }, [open]);

  // ── Computed ────────────────────────────────────────────────────────────────

  const filteredTemplates = useMemo(() => {
    const out = templates.filter((t) => {
      const q = templateSearch.trim().toLowerCase();
      return (
        q.length === 0 ||
        t.title.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        t.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    });
    out.sort((a, b) =>
      templateSort === 'title'
        ? a.title.localeCompare(b.title)
        : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    return out;
  }, [templates, templateSearch, templateSort]);

  const filteredMyRubrics = useMemo(
    () => myRubrics.filter((r) => r.title.toLowerCase().includes(rubricSearch.toLowerCase())),
    [myRubrics, rubricSearch]
  );
  const filteredSharedRubrics = useMemo(
    () => sharedRubrics.filter((r) => r.title.toLowerCase().includes(rubricSearch.toLowerCase())),
    [sharedRubrics, rubricSearch]
  );

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleUseTemplate = async (templateId: string) => {
    const created = await onPickTemplate({ templateId });
    onClose();
    if (created) onCreatedResource?.(created);
  };

  const handleToggleGenerate = (templateId: string) => {
    if (contextInputId === templateId) {
      setContextInputId(null);
      setUserContext('');
      setGenerationError(null);
    } else {
      setContextInputId(templateId);
      setUserContext('');
      setGenerationError(null);
    }
  };

  const handleGenerate = async (templateId: string) => {
    setGeneratingId(templateId);
    setGenerationError(null);
    try {
      const result = await onGenerateFromTemplate({
        templateId,
        parentCourseId: locator.parentCourseId,
        parentUnitId: locator.parentUnitId,
        parentLessonId: locator.parentLessonId,
        courseId: locator.courseId,
        userContext: userContext.trim(),
      });
      if (result?.resourceId) {
        setGeneratingId(null);
        onClose();
        onGeneratedResource?.(result);
        return;
      }
      setGeneratingId(null);
    } catch (error) {
      setGenerationError(getGenerationErrorMessage(error));
      setGeneratingId(null);
    }
  };

  const handleAttachRubric = async (rubric: Rubric) => {
    setRubricAttachError(null);
    try {
      await attachLibraryRubric({ ...locator, rubricId: rubric.id }).unwrap();
      onClose();
    } catch {
      setRubricAttachError('Failed to attach rubric. Please try again.');
    }
  };

  const handleGoogleDriveError = (message: string | null) => {
    setGoogleDriveError(message);
    if (message) setNativeImportInfo(null);
    onGoogleDriveError?.(message);
  };

  const handleNativeImportComplete = (
    result: {
      totalWarnings: number;
      importedCount: number;
      failedCount: number;
    },
    noun = 'presentation'
  ) => {
    if (result.importedCount > 0) {
      const importedMessage = `Imported ${result.importedCount} ${noun}${result.importedCount !== 1 ? 's' : ''}`;
      const warningMessage =
        result.totalWarnings > 0
          ? ` with ${result.totalWarnings} warning${result.totalWarnings !== 1 ? 's' : ''} (some elements may not have imported perfectly)`
          : '';
      const failureMessage =
        result.failedCount > 0
          ? `; ${result.failedCount} file${result.failedCount !== 1 ? 's' : ''} failed`
          : '';
      setNativeImportInfo(`${importedMessage}${warningMessage}${failureMessage}.`);
    } else {
      setNativeImportInfo(null);
    }
    onNativeImportComplete?.(result);
  };

  const handleGoogleDriveImportComplete = () => {
    onClose();
  };

  // ── Sidebar items ────────────────────────────────────────────────────────────

  const sidebarItems: Array<{ id: SidebarCategoryId; label: string; Icon: SvgIconComponent }> = [
    { id: 'new', label: 'New', Icon: AddCircleOutlineIcon },
    ...(rubricsEnabled ? [{ id: 'rubrics' as const, label: 'Rubrics', Icon: RuleIcon }] : []),
    ...(templates.length > 0
      ? [{ id: 'templates' as const, label: 'Templates', Icon: FileCopyIcon }]
      : []),
  ];

  // ── Renders ──────────────────────────────────────────────────────────────────

  const renderNewSection = () => (
    <Box>
      {/* Create */}
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Create
      </Typography>
      <Box sx={TILE_GRID_SX}>
        <ActionTile icon={DescriptionOutlinedIcon} label="New Document" onClick={onNewDocument} />
        <ActionTile icon={SlideshowOutlinedIcon} label="New Slide Deck" onClick={onNewSlides} />
        {rubricsEnabled && <ActionTile icon={RuleIcon} label="New Rubric" onClick={onNewRubric} />}
      </Box>

      {/* Import */}
      <Typography variant="overline" color="text.secondary" sx={{ mt: 3, mb: 1, display: 'block' }}>
        Import
      </Typography>
      {googleDriveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => handleGoogleDriveError(null)}>
          {googleDriveError}
        </Alert>
      )}
      {nativeImportInfo && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNativeImportInfo(null)}>
          {nativeImportInfo}
        </Alert>
      )}
      <Box sx={TILE_GRID_SX}>
        <ActionTile icon={LinkIcon} label="Add Link" onClick={onAddLink} />
        <ActionTile icon={UploadFileIcon} label="Upload File" onClick={onUploadFile} />
        {showGoogleDrive && (
          <GoogleDrivePickerButton
            locator={locator}
            tileMode
            mode="link"
            onError={handleGoogleDriveError}
            onImportComplete={handleGoogleDriveImportComplete}
          />
        )}
        {showImportSlides && (
          <GoogleDrivePickerButton
            locator={locator}
            tileMode
            mode="native"
            onError={handleGoogleDriveError}
            onNativeImportComplete={handleNativeImportComplete}
          />
        )}
        {showImportDocs && (
          <GoogleDrivePickerButton
            locator={locator}
            tileMode
            mode="native-docs"
            onError={handleGoogleDriveError}
            onNativeImportComplete={(result) => handleNativeImportComplete(result, 'document')}
          />
        )}
      </Box>
    </Box>
  );

  const renderRubricCard = (rubric: Rubric) => (
    <Paper
      key={rubric.id}
      variant="outlined"
      onClick={attachingRubric ? undefined : () => void handleAttachRubric(rubric)}
      sx={{
        p: 1.5,
        cursor: attachingRubric ? 'default' : 'pointer',
        opacity: attachingRubric ? 0.7 : 1,
        '&:hover': attachingRubric ? {} : { bgcolor: 'action.hover', borderColor: 'primary.main' },
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
          {rubricTypeIcon(rubric.content)}
        </Box>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
          {rubric.title}
        </Typography>
        <Chip label={rubricTypeLabel(rubric.content)} size="small" variant="outlined" />
      </Stack>
    </Paper>
  );

  const renderRubricsSection = () => {
    const rubricsFetching = myRubricsFetching || sharedRubricsFetching;
    return (
      <Box>
        {/* New Rubric tile */}
        <Box sx={TILE_GRID_SX} mb={3}>
          <ActionTile icon={RuleIcon} label="New Rubric" onClick={onNewRubric} />
        </Box>
        <TextField
          size="small"
          placeholder="Search rubrics..."
          value={rubricSearch}
          onChange={(e) => setRubricSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
        />
        {rubricAttachError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRubricAttachError(null)}>
            {rubricAttachError}
          </Alert>
        )}
        {rubricsFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* My Rubrics */}
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              My Rubrics
            </Typography>
            {filteredMyRubrics.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {rubricSearch ? 'No rubrics match your search.' : 'You have no rubrics yet.'}
              </Typography>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 1,
                  mb: 2,
                }}
              >
                {filteredMyRubrics.map(renderRubricCard)}
              </Box>
            )}

            {/* Shared with Me */}
            {(sharedRubrics.length > 0 || filteredSharedRubrics.length > 0) && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Shared with Me
                </Typography>
                {filteredSharedRubrics.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {rubricSearch
                      ? 'No shared rubrics match your search.'
                      : 'No rubrics have been shared with you.'}
                  </Typography>
                ) : (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                      gap: 1,
                    }}
                  >
                    {filteredSharedRubrics.map(renderRubricCard)}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderTemplatesSection = () => (
    <Box>
      <Stack direction="row" spacing={1.25} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={templateSearch}
          onChange={(e) => setTemplateSearch(e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          select
          label="Sort"
          value={templateSort}
          onChange={(e) => setTemplateSort(e.target.value as 'title' | 'recent')}
          sx={{ minWidth: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="title">Title A–Z</option>
          <option value="recent">Recently Updated</option>
        </TextField>
      </Stack>

      {filteredTemplates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No templates match your filters.
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {filteredTemplates.map((template) => {
            const isExpanded = contextInputId === template.id;
            const isGenerating = generatingId === template.id;
            return (
              <Card key={template.id} variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {template.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {template.description || 'No description'}
                      </Typography>
                    </Box>
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileCopyIcon />}
                        onClick={() => void handleUseTemplate(template.id)}
                      >
                        Use Template
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => handleToggleGenerate(template.id)}
                        disabled={isGenerating || disableGenerate}
                      >
                        Generate
                      </Button>
                    </Stack>
                    <Collapse in={isExpanded}>
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        <TextField
                          size="small"
                          label="Context for AI (optional)"
                          placeholder="E.g. Make it about the water cycle for 5th graders"
                          value={userContext}
                          onChange={(e) => setUserContext(e.target.value)}
                          multiline
                          minRows={2}
                          maxRows={4}
                          fullWidth
                          disabled={isGenerating}
                        />
                        {generationError && (
                          <Typography variant="caption" color="error">
                            {generationError}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            isGenerating ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <AutoAwesomeIcon />
                            )
                          }
                          onClick={() => void handleGenerate(template.id)}
                          disabled={isGenerating}
                        >
                          {isGenerating ? 'Generating…' : 'Generate Resource'}
                        </Button>
                      </Stack>
                    </Collapse>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );

  const renderContent = () => {
    if (activeCategory === 'new') return renderNewSection();
    if (activeCategory === 'rubrics') return renderRubricsSection();
    if (activeCategory === 'templates') return renderTemplatesSection();
    return null;
  };

  const SIDEBAR_WIDTH = 200;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 64px)' },
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>Add Resource</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          gap: 0,
          p: 0,
          pt: '8px !important',
          flex: '1 1 520px',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* ── Sidebar ── */}
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflowY: 'auto',
            py: 1,
            px: 1,
          }}
        >
          <List dense disablePadding>
            {sidebarItems.map((item) => (
              <ListItemButton
                key={item.id}
                selected={activeCategory === item.id}
                onClick={() => setActiveCategory(item.id)}
                sx={{ borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <item.Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>{renderContent()}</Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedResourcePickerDialog;
